import { useEffect, useState } from "react";
import { Portal, useMediaQuery, useTheme } from "@mui/material";
import Confetti from "react-confetti";

const FALLBACK_COLORS = ["#3A7DC9", "#5BB8F5", "#1A3F7A", "#F4A11A", "#FFC14D", "#19A874", "#FFFFFF"];

// Width of each shower column, as a fraction of the viewport. The middle is
// left clear so the avatar and copy stay readable behind the fall.
const COLUMN_WIDTH = 0.28;

// Stop spawning this far before the close so the last pieces fall out of frame
// instead of vanishing mid-air.
const FALLOUT_MS = 4000;

function useWindowSize() {
	const [size, setSize] = useState(() => ({
		width: typeof window === "undefined" ? 0 : window.innerWidth,
		height: typeof window === "undefined" ? 0 : window.innerHeight,
	}));

	useEffect(() => {
		const onResize = () => setSize({ width: window.innerWidth, height: window.innerHeight });

		window.addEventListener("resize", onResize);
		onResize();

		return () => window.removeEventListener("resize", onResize);
	}, []);

	return size;
}

/**
 * Two rain columns pinned to the left and right edges, framing the centred
 * modal. react-confetti takes a single confettiSource, so each side is its own
 * instance rather than one wide source that would rain over the card.
 */
const ConfettiCanvas = ({ duration = 20000, active = true, colors = FALLBACK_COLORS }) => {
	const { width, height } = useWindowSize();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const [recycle, setRecycle] = useState(true);

	useEffect(() => {
		if (!active) return undefined;

		setRecycle(true);
		const timer = setTimeout(() => setRecycle(false), Math.max(0, duration - FALLOUT_MS));

		return () => clearTimeout(timer);
	}, [active, duration, colors]);

	if (!active || !width || !height) return null;

	const columnWidth = width * COLUMN_WIDTH;
	const pieces = isMobile ? 70 : 130;

	const shared = {
		width,
		height,
		numberOfPieces: pieces,
		recycle,
		colors,
		gravity: 0.12,
		friction: 0.99,
		initialVelocityY: { min: 0, max: 8 },
		initialVelocityX: { min: -4, max: 4 },
		tweenDuration: 3000,
		style: {
			// react-confetti defaults to position:absolute / zIndex:2, which puts
			// it behind the modal backdrop. Both have to be overridden.
			position: "fixed",
			top: 0,
			left: 0,
			pointerEvents: "none",
			zIndex: theme.zIndex.modal + 2,
		},
	};

	return (
		<Portal>
			{/* Each column drifts gently inward, so the fall gathers toward the card. */}
			<Confetti {...shared} confettiSource={{ x: 0, y: -20, w: columnWidth, h: 0 }} wind={0.008} />
			<Confetti {...shared} confettiSource={{ x: width - columnWidth, y: -20, w: columnWidth, h: 0 }} wind={-0.008} />
		</Portal>
	);
};

export default ConfettiCanvas;
